import { useState } from 'react';
import { betterAuthClient } from '@/lib/auth-client';
import { authErrorMessage } from '@/lib/authError';
import { Alert, Button, Muted, TextInput } from './ui';

/**
 * `oauthCallbackURL` runs just before the Google redirect leaves the page and names where
 * Google sign-in lands; without it, `/`. `verifyCallbackURL` is where the link in the sign-up
 * verification email lands; better-auth defaults it to `/`. A failed Google sign-in lands on
 * `oauthErrorURL` with `?error=<code>`, which the page passes back in as `initialError`.
 */
export function SignInForm({
  oauthCallbackURL,
  verifyCallbackURL,
  oauthErrorURL = '/',
  initialError,
}: {
  oauthCallbackURL?: () => string;
  verifyCallbackURL?: string;
  oauthErrorURL?: string;
  initialError?: string | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() =>
    initialError ? authErrorMessage(initialError, `Google sign-in failed (${initialError}).`) : '',
  );
  const [verifySentTo, setVerifySentTo] = useState('');
  const [busy, setBusy] = useState(false);

  const emailAuth = async (mode: 'signIn' | 'signUp') => {
    if (busy) return;
    setBusy(true);
    setError('');
    setVerifySentTo('');
    try {
      // better-auth reports rejections (existing user, bad password) in the result, not by throwing.
      const result =
        mode === 'signIn'
          ? await betterAuthClient.signIn.email({ email, password })
          : await betterAuthClient.signUp.email({
              email,
              password,
              name: email,
              callbackURL: verifyCallbackURL,
            });
      if (result.error) setError(authErrorMessage(result.error, 'Sign-in failed'));
      // A sign-up that needs a verified email creates the user with no session.
      else if (mode === 'signUp' && !result.data?.token) setVerifySentTo(email);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Sign in"
      className="rounded-2xl border border-divider bg-surface-elevated p-4"
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void emailAuth('signIn');
        }}
      >
        <TextInput
          aria-label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          inputMode="email"
          autoComplete="email"
        />
        <TextInput
          aria-label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
        />
        <Muted>
          Accounts are by invitation. Sign up with the email address your invite was sent to.
        </Muted>
        {error && <Alert>{error}</Alert>}
        {verifySentTo && (
          <Muted>We sent a verification link to {verifySentTo}. Open it, then sign in.</Muted>
        )}
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>
            Sign in
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => void emailAuth('signUp')}>
            Sign up
          </Button>
        </div>
        <button
          type="button"
          onClick={() => {
            const callbackURL = oauthCallbackURL ? oauthCallbackURL() : '/';
            void betterAuthClient.signIn.social({
              provider: 'google',
              callbackURL,
              errorCallbackURL: oauthErrorURL,
            });
          }}
          className="w-full rounded-xl border border-divider px-4 py-2.5 text-sm font-medium text-content-secondary hover:bg-interactive-hover"
        >
          Continue with Google
        </button>
      </form>
    </section>
  );
}
